import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { NoWebshopState, webshopErrorText } from './_webshopNotice';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    TextField,
    Tooltip,
    Typography,
    CircularProgress,
} from '@mui/material';
import LabelIcon from '@mui/icons-material/Label';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import RestorePageIcon from '@mui/icons-material/RestorePage';
import LinkIcon from '@mui/icons-material/Link';
import LanguageIcon from '@mui/icons-material/Language';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import StorageIcon from '@mui/icons-material/Storage';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloneWebshopDialog from './CloneWebshopDialog';
import TestVersionDialog from './TestVersionDialog';
import { ResendMailDialog } from './ReservationActions';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import Colors from '../../library/Colors';
import SftGlobalData from '../../library/SftGlobalData';
import { usePermissions } from '../../library/Permissions';
import { actionPermKey } from '../../library/permissionCatalog';
import { webshopTokens, iconBoxSx, WebshopPageHeader } from './_webshopUi';

const WebshopActionsScreen = (props) => {
    const navigate = useNavigate();
    useEffect(() => {
        // Header + simulator keep working via startsWith('webshop').
        if (props.changeScreen) props.changeScreen('webshop-actions');
    }, []);

    const tr = (key, fb) => (props.t ? props.t(key, fb) : fb);
    // SoftTouch user (any group) vs customer: sft_status "0" = customer, as
    // elsewhere in the app (App.js environment choice, LeftSidebar).
    const isSftUser = String(props.login?.sft_status ?? '0') !== '0';
    // Per-action role gate (roles dialog › Acties); on top of the
    // SoftTouch-only checks, never instead of them.
    const { can } = usePermissions();

    const [resendModalOpen, setResendModalOpen] = useState(false);
    const [updateUrlModalOpen, setUpdateUrlModalOpen] = useState(false);
    const [newUrl, setNewUrl] = useState('');
    const [urlBusy, setUrlBusy] = useState(false);
    const [syncBusy, setSyncBusy] = useState(false);
    const [cloneOpen, setCloneOpen] = useState(false);
    const [testVerOpen, setTestVerOpen] = useState(false);

    const { isDark, cardBg, borderColor, textColor, mutedText } = webshopTokens(props.theme);

    const notify = (msg, severity = 1) => {
        if (props.notify) props.notify(msg, 4000, 'top-right', props.theme, severity);
    };

    // The webshop's URL lives on `display_name` in `lic_clients_webshop`. The
    // auth backend stores it as CONCAT(cron_url, '/server/v1/mini-api'); the
    // legacy scripts live at `${cron_url}${path}`, so strip the mini-api
    // suffix to get a clean base.
    const baseUrl = useMemo(() => {
        const url = props.selectedWebshop?.display_name || '';
        return url.replace(/\/server\/v1\/mini-api\/?$/i, '').replace(/\/$/, '');
    }, [props.selectedWebshop?.display_name]);

    // A row that still says http:// works (the proxy upgrades the scheme
    // itself) but is untidy and used to break every mini-api call: the shop's
    // http->https redirect drops the Authorization header. Flag it and offer
    // the one-click fix via the existing setUrl action.
    const insecureUrl = useMemo(() => {
        const fields = [props.selectedWebshop?.display_name, props.selectedWebshop?.url];
        // Hoster preview hosts (….webhosting.be) have a self-signed cert until the
        // real domain is live: http is correct there, so no warning and no fix.
        return fields.some(v => typeof v === 'string'
            && /^http:\/\//i.test(v.trim())
            && !/^http:\/\/([^/]+\.)?webhosting\.be(\/|:|$)/i.test(v.trim()));
    }, [props.selectedWebshop?.display_name, props.selectedWebshop?.url]);
    const [fixingScheme, setFixingScheme] = useState(false);
    const fixScheme = async () => {
        if (!props.selectedWebshop?.id) return;
        setFixingScheme(true);
        try {
            const httpsUrl = 'https://' + baseUrl.replace(/^https?:\/\//i, '');
            const r = await SftGlobalData.post('webshop&action=setUrl', {
                key: props.api,
                webshop_id: props.selectedWebshop.id,
                url: httpsUrl,
            });
            if (r?.success) {
                notify(tr('cfg_act_https_fixed', 'Webshop URL now uses https.'), 1);
                if (props.onProvisioned) props.onProvisioned();
            } else {
                notify(r?.message || tr('cfg_act_https_fix_failed', 'Could not update the URL.'), 3);
            }
        } catch (e) {
            notify(e?.message || tr('cfg_act_https_fix_failed', 'Could not update the URL.'), 3);
        } finally {
            setFixingScheme(false);
        }
    };

    // Build a legacy URL with the standard cache-buster the legacy admin uses
    // (some endpoints, e.g. /admin/schedule-slug-reset, won't fire a fresh
    // job without it because the browser serves a cached response).
    const buildLegacyUrl = (path) => {
        const sep = path.includes('?') ? '&' : '?';
        return `${baseUrl}${path}${sep}__cb_=${Date.now()}`;
    };

    // Open a legacy storefront URL in a new tab. The user is normally already
    // logged into the legacy admin in another tab, so the script picks up
    // their session there. The script's progress output stays visible for
    // diagnostics, the MySft tab keeps its state.
    const openLegacyAction = (path) => {
        if (!baseUrl) { notify(tr('cfg_act_no_webshop_selected', 'No webshop selected'), 1); return; }
        if (!path)    { notify(tr('cfg_act_not_wired', 'Action not wired yet'), 1); return; }
        window.open(buildLegacyUrl(path), '_blank', 'noopener,noreferrer');
    };

    // Resend goes through the mini-api (POST /reservations/resend-mail, with
    // login + audit) — no longer through the shop's public
    // scripts/send-reservation-email.php in a hidden iframe.
    const proxyCall = (method, path, body) => SftGlobalData.post('webshop&action=proxy', {
        key:        props.api,
        webshop_id: props.selectedWebshop?.id,
        sftUserId:  props.login?.sft_user_id,
        user:       props.login?.login,
        method,
        path,
        ...(body ? { body } : {}),
    });

    /**
     * Copy what the shop reported (lic_clients_webshop_info, filled by the
     * webshop_info cron) into lic_clients_webshop -- the table this admin and
     * the mini-api proxy actually read. Scoped to the key's own client, and
     * idempotent on (client_id, db_id, slug), so re-running is harmless.
     *
     * Used on its own ("sync webshop record") and as the last step of the URL
     * change.
     *
     * @returns {Promise<boolean>} whether a webshop came back
     */
    const syncWebshopRecord = async ({ okMessage, emptyMessage } = {}) => {
        const res = await SftGlobalData.post('webshop&action=provision', { key: props.api });
        const list = Array.isArray(res?.data) ? res.data : [];
        if (res?.success && list.length > 0) {
            notify(okMessage || tr('cfg_act_sync_record_done', 'Webshop details synced. The list has been refreshed.'), 2);
            if (props.onProvisioned) props.onProvisioned();
            return list;
        }
        if (res?.success) {
            notify(emptyMessage || tr('cfg_act_sync_record_empty', 'Nothing in the sync data yet — the shop has not reported itself. Check that its cron runs.'), 1);
            return null;
        }
        notify(res?.message || tr('cfg_act_url_sync_failed', 'Syncing the licence database failed.'), 1);
        return null;
    };

    // Slug reset and full product sync go through the mini-api
    // (POST /admin/schedule-*), which drops the sentinel the next cron run
    // picks up — as the legacy admin did. The work then runs from cron, not
    // inside a browser tab that can time out halfway. (The legacy slug route is
    // POST-only, so opening it in a tab never scheduled anything.)
    const [scheduleBusy, setScheduleBusy] = useState(null);
    const runSchedule = async ({ id, path, label, done, confirm }) => {
        if (!props.selectedWebshop?.id) { notify(tr('cfg_act_no_webshop_selected', 'No webshop selected'), 1); return; }
        if (confirm && !window.confirm(confirm)) return;
        setScheduleBusy(id);
        try {
            const r = await SftGlobalData.post('webshop&action=proxy', {
                key:        props.api,
                webshop_id: props.selectedWebshop.id,
                sftUserId:  props.login?.sft_user_id,
                user:       props.login?.login,
                method:     'POST',
                path,
                body:       {},
            });
            if (r?.success) {
                notify(done, 2);
            } else if (r?.data?.status === 409) {
                // the sentinel is still there: the previous request has not run yet
                notify(`${label}: ${tr('cfg_act_already_scheduled', 'already scheduled — it has not run yet.')}`, 1);
            } else {
                notify(`${label}: ${webshopErrorText(r, tr) || tr('cfg_act_failed', 'failed')}`, 1);
            }
        } catch (e) {
            notify(`${label}: ${e?.message || tr('cfg_act_failed', 'failed')}`, 1);
        } finally {
            setScheduleBusy(null);
        }
    };
    const runSlugReset = () => runSchedule({
        id:      'slug-reset',
        path:    '/admin/schedule-slug-reset',
        label:   tr('cfg_act_slug_reset_title', 'Slug reset'),
        done:    tr('cfg_act_slug_reset_done', 'Slug reset scheduled — it runs with the next attribute sync.'),
        confirm: tr('cfg_act_slug_reset_confirm', 'This clears the URL slugs of all categories; the next attribute sync rebuilds them from the current names. Existing links to category pages may stop working. Continue?'),
    });
    const runFullSync = () => runSchedule({
        id:    'full-product-sync',
        path:  '/admin/schedule-full-sync',
        label: tr('cfg_act_product_sync_title', 'Force full product sync'),
        done:  tr('cfg_act_product_sync_done', 'Full sync scheduled — it runs with the next product sync.'),
    });

    const runSyncWebshopRecord = async () => {
        setSyncBusy(true);
        notify(`${tr('cfg_act_sync_record_title', 'Sync webshop details')}: ${tr('cfg_act_running', 'running…')}`, 0);
        try {
            await syncWebshopRecord();
        } catch (e) {
            notify(`${tr('cfg_act_sync_record_title', 'Sync webshop details')}: ${e?.message || tr('cfg_act_failed', 'failed')}`, 1);
        } finally {
            setSyncBusy(false);
        }
    };

    /**
     * Move a shop to a new domain, in the three steps this actually takes:
     *
     *  1. `scripts/replace_urls.php` on the shop rewrites the old domain in the
     *     DB rows that store absolute URLs (carousel, external_links,
     *     image_grid, information_banners, settings), in statics/ and in the
     *     cheque layouts, then regenerates the statics.
     *  2. `crons/webshop_info.php` reports the shop upstream, and it builds
     *     `cron_url` from the host it is called on -- so it has to run on the
     *     NEW domain, otherwise the old one keeps being reported.
     *  3. auth `webshop&action=provision` copies that cron_url from
     *     lic_clients_webshop_info into lic_clients_webshop (scoped to the key,
     *     idempotent), which is the table this admin and the mini-api proxy
     *     read.
     *
     * Step 1 is not reversible from here, hence the confirm in the dialog.
     */
    const updateWebshopUrl = async () => {
        const target = newUrl.trim().replace(/\/+$/, '');
        if (!target) {
            notify(tr('cfg_act_enter_new_url', 'Please enter a new URL'), 1);
            return;
        }
        if (!baseUrl) {
            notify(tr('cfg_act_no_webshop_selected', 'No webshop selected'), 1);
            return;
        }
        if (!/^https?:\/\//i.test(target)) {
            notify(tr('cfg_act_url_needs_scheme', 'Enter the full URL, including https://'), 1);
            return;
        }
        if (target.toLowerCase() === baseUrl.toLowerCase()) {
            notify(tr('cfg_act_url_unchanged', 'That is already the current URL'), 1);
            return;
        }

        setUrlBusy(true);
        try {
            // Step 0 — the FTP-side half: ENV_PRODUCTION in config.php, the
            // Sitemap line in robots.txt and the old-host lines in .crontab.
            // Runs first: everything after this assumes the new domain is the
            // one the shop identifies with.
            notify(`${tr('cfg_act_update_url_title', 'Update webshop URL')}: ${tr('cfg_act_url_step_golive', 'updating config, robots.txt and crontab…')}`, 0);
            const golive = await SftGlobalData.post('webshop&action=golive', {
                key: props.api,
                webshop_id: props.selectedWebshop?.id,
                url: target,
            });
            if (!golive?.success) {
                // Report which step failed, then stop: rewriting content URLs
                // while the shop still identifies as the old domain would only
                // make the mess bigger.
                const steps = golive?.data?.steps || [];
                const failed = steps.filter(st => st && st.ok === false).map(st => `${st.step}: ${st.msg}`);
                notify(failed.length
                    ? `${tr('cfg_act_url_golive_failed', 'Server-side go-live failed')} — ${failed.join(' | ')}`
                    : (golive?.message || tr('cfg_act_url_golive_failed', 'Server-side go-live failed')), 1);
                return;
            }

            // Steps 1 and 2 (rewrite URLs on the shop, report the new domain)
            // are part of auth's golive now: they ran from this browser before,
            // which fails on the usual old URL (http preview host -> "mixed
            // content" on an https admin). Auth talks to the shop over http
            // without that restriction. A failed report is best-effort there.
            const reportStep = (golive?.data?.steps || []).find(st => st && st.step === 'report');
            if (reportStep && /nog niet|not answer/i.test(reportStep.msg || '')) {
                notify(tr('cfg_act_url_report_failed', 'The new domain did not answer yet — run the sync again once it does.'), 1);
            }

            notify(`${tr('cfg_act_update_url_title', 'Update webshop URL')}: ${tr('cfg_act_url_step_sync', 'syncing the licence database…')}`, 0);
            const synced = await syncWebshopRecord({
                okMessage: tr('cfg_act_update_url_done', 'URL updated. The webshop list has been refreshed.'),
                emptyMessage: tr('cfg_act_url_sync_empty', 'URLs were rewritten, but the new domain is not in the sync data yet. Re-run this once the shop has reported.'),
            });
            if (synced) {
                // ENV_PRODUCTION doubles as the environment, and that value is
                // the `slug` the licence row is keyed on -- so a go-live yields
                // a NEW row next to the old one. Retire the old one (soft
                // delete, credentials carried over) and point the admin at the
                // new one, otherwise every screen keeps talking to the old
                // domain and the shop shows up twice in the picker.
                let list = synced;
                try {
                    const retired = await SftGlobalData.post('webshop&action=retireOldUrl', {
                        key: props.api,
                        old_url: baseUrl,
                        new_url: target,
                    });
                    if (retired?.success) {
                        if (Array.isArray(retired.data?.webshops)) list = retired.data.webshops;
                        if (retired.data?.retired > 0) {
                            notify(retired.data.credentials_moved
                                ? tr('cfg_act_url_retired_creds', 'The old entry has been retired and its FTP/DB credentials moved to the new one.')
                                : tr('cfg_act_url_retired', 'The old entry has been retired.'), 2);
                        }
                    }
                } catch {
                    // Not fatal: the URL change itself already succeeded.
                    notify(tr('cfg_act_url_retire_failed', 'Could not retire the old entry — remove it manually if the shop shows up twice.'), 1);
                }

                const host = target.replace(/^https?:\/\//i, '').toLowerCase();
                const match = list.find(w => String(w.display_name || w.url || '').toLowerCase().includes(host));
                if (match && props.onSelectWebshop) {
                    props.onSelectWebshop(match);
                } else if (!match) {
                    notify(tr('cfg_act_url_no_new_row', 'The new domain has no entry in the licence database yet — pick it in the webshop selector once it appears.'), 1);
                }
                setUpdateUrlModalOpen(false);
                setNewUrl('');
            }
        } catch (e) {
            notify(`${tr('cfg_act_update_url_title', 'Update webshop URL')}: ${e?.message || tr('cfg_act_failed', 'failed')}`, 1);
        } finally {
            setUrlBusy(false);
        }
    };

    // ── Action registry ────────────────────────────────────────────────────
    // path = legacy storefront URL relative to the webshop's cron_url.
    // Buttons with `path` open `${baseUrl}${path}` in a new tab; buttons
    // without are intentionally left unwired (no documented endpoint yet)
    // and render a disabled "Run" with a "Not wired yet" badge.
    const ACTIONS = [
        {
            id: 'attr-sync',
            icon: LabelIcon,
            title: tr('cfg_act_attr_sync_title', 'Force attribute sync'),
            description: tr('cfg_act_attr_sync_desc', 'Re-push all product attributes (size, color, material…) from the POS to the webshop.'),
            path: '/server/v1/scripts/attributes/sync',
        },
        {
            // Run now = the shop's own script in a new tab (as the old admin's
            // report link did); it holds the DB lock `sync_products`, so a sync
            // already running is not started twice (it schedules itself
            // instead). Schedule = the next modified run does it.
            id: 'full-product-sync',
            icon: Inventory2Icon,
            title: tr('cfg_act_product_sync_title', 'Force full product sync'),
            description: tr('cfg_act_product_sync_desc', 'Rebuild the entire webshop product catalog — prices, stock, descriptions, everything.'),
            path: '/server/v1/scripts/products/sync',
            runLabel: tr('cfg_act_run_now', 'Run now'),
            secondary: {
                label: tr('cfg_act_schedule', 'Schedule'),
                tip: tr('cfg_act_product_sync_schedule_tip', 'The next automatic product sync (within about 10 minutes) does the full sync.'),
                onClick: runFullSync,
                busy: () => scheduleBusy === 'full-product-sync',
            },
        },
        {
            id: 'brand-sync',
            icon: BrandingWatermarkIcon,
            title: tr('cfg_act_brand_sync_title', 'Force brand logo sync'),
            description: tr('cfg_act_brand_sync_desc', 'Re-upload every brand logo to the webshop from the POS catalog.'),
            path: '/server/v1/crons/brand_logo_sync.php',
        },
        {
            id: 'image-sync',
            icon: PhotoLibraryIcon,
            title: tr('cfg_act_image_sync_title', 'Force image sync'),
            description: tr('cfg_act_image_sync_desc', 'Re-push all product images to the webshop. Use when product images appear broken or missing.'),
            path: '/server/v1/scripts/images/process',
        },
        {
            id: 'reservation-sync',
            icon: ReceiptLongIcon,
            title: tr('cfg_act_reservation_sync_title', 'Process queued reservations'),
            description: tr('cfg_act_reservation_sync_desc', 'Re-send pending reservations and receipts that failed to reach the webshop.'),
            path: '/server/v1/crons/process_queued_reservations.php',
        },
        {
            id: 'resend-transaction',
            icon: ForwardToInboxIcon,
            title: tr('cfg_act_resend_title', 'Resend reservation / receipt'),
            description: tr('cfg_act_resend_desc', 'Send the order mail of a reservation again, to the customer (or another address) and/or the store. Also per row in Data › Reservations.'),
            onClick: () => setResendModalOpen(true),
        },
        {
            id: 'reset-images',
            icon: RestorePageIcon,
            title: tr('cfg_act_reset_images_title', 'Reset images'),
            description: tr('cfg_act_reset_images_desc', 'Move product images back to the upload folder. They will be re-processed on the next sync.'),
            destructive: true,
            // The shop's own script: moves img-repository/original back to
            // uploads (max 30 min per run); the image cron then rebuilds the
            // processed sizes, which stay online meanwhile.
            path: '/server/v1/scripts/move_images_to_original.php',
            onClick: () => {
                if (!window.confirm(tr('cfg_act_reset_images_confirm', 'All original product photos go back to the upload folder and are processed again by the image cron — for a large catalogue that can take hours. The current photos stay online meanwhile. Continue?'))) return;
                openLegacyAction('/server/v1/scripts/move_images_to_original.php');
            },
        },
        {
            id: 'slug-reset',
            icon: LinkIcon,
            title: tr('cfg_act_slug_reset_title', 'Slug reset'),
            description: tr('cfg_act_slug_reset_desc', 'Clear the URL slugs of all categories; the next attribute sync rebuilds them. Existing links to category pages may break.'),
            destructive: true,
            onClick: runSlugReset,
            busy: () => scheduleBusy === 'slug-reset',
        },
        {
            id: 'sync-webshop-record',
            icon: CloudSyncIcon,
            title: tr('cfg_act_sync_record_title', 'Sync webshop details'),
            description: tr('cfg_act_sync_record_desc', 'Pull what the shop reported (URL, environment) into the licence database. Use this after a domain change or when the shop is missing from the list.'),
            onClick: runSyncWebshopRecord,
            busy: () => syncBusy,
        },
        {
            id: 'update-url',
            icon: LanguageIcon,
            title: tr('cfg_act_update_url_title', 'Update webshop URL'),
            description: tr('cfg_act_update_url_desc2', 'Move the shop to a new domain: rewrite the stored URLs, let the shop report itself, and update the licence database.'),
            destructive: true,
            onClick: () => setUpdateUrlModalOpen(true),
        },
        {
            id: 'update-version',
            icon: SystemUpdateAltIcon,
            title: tr('cfg_act_update_version_title', 'Updates uitvoeren'),
            description: tr('cfg_act_update_version_desc', 'Open de setup-wizard om de laatste updates (mini-api + migraties) op de shop uit te voeren. Veilig — raakt geen data.'),
            onClick: () => navigate('/' + (props.lang || 'nl') + '/webshop-setup'),
        },
        {
            // SoftTouch SUPER only: changes the shop's database schema. Runs
            // the migrations that have not run yet (always "up" — never pass a
            // direction); the script's own output opens in the new tab.
            id: 'run-migrations',
            icon: StorageIcon,
            title: tr('cfg_act_migrations_title', 'Run migrations'),
            description: tr('cfg_act_migrations_desc', 'Run the pending database migrations of the shop. Normally the update does this; use it when an update stopped halfway. SoftTouch only.'),
            destructive: true,
            superOnly: true,
            path: '/server/v1/migrations/run.php',
            onClick: () => {
                if (!window.confirm(tr('cfg_act_migrations_confirm', 'This runs the pending database migrations on the shop. Continue?'))) return;
                openLegacyAction('/server/v1/migrations/run.php');
            },
        },
        {
            // compare.fashionretail.eu = legacy-client twin of php8 (2026-10-01).
            // Every SoftTouch user, not just SUPER (owner 2026-10-06).
            id: 'clone-webshop',
            icon: ContentCopyIcon,
            title: tr('cfg_act_clone_title', 'Clone webshop'),
            description: tr('cfg_act_clone_desc', 'Replace this webshop with a copy of another webshop of the same client on the same hosting account: database, images, designs and content files. For a test twin. SoftTouch only.'),
            destructive: true,
            sftOnly: true,
            onClick: () => setCloneOpen(true),
        },
        {
            // test channel: builds published with tools/release-test.sh (storefront repo)
            id: 'test-version',
            icon: ScienceOutlinedIcon,
            title: tr('cfg_act_testver_title', 'Install test version'),
            description: tr('cfg_act_testver_desc', 'Install a build from the test channel on this webshop. Only builds released for this domain and its client can be installed; a live shop never takes a test build. SoftTouch only.'),
            destructive: true,
            superOnly: true,
            onClick: () => setTestVerOpen(true),
        },
    ];

    // No webshop linked → the action cards would all be disabled anyway; show
    // an explicit empty-state instead.
    if (!props.selectedWebshop) {
        return (
            <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
                <Box sx={{ border: `1px solid ${borderColor}`, borderRadius: 2, backgroundColor: cardBg, p: 3 }}>
                    <NoWebshopState t={props.t} api={props.api} onProvisioned={props.onProvisioned} notify={props.notify} theme={props.theme} />
                </Box>
            </div>
        );
    }

    return (
        <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
            <WebshopPageHeader
                t={props.t}
                theme={props.theme}
                title={tr('cfg_act_title', 'Webshop actions')}
                description={tr('cfg_act_desc', 'Admin actions that trigger server-side syncs or maintenance jobs. Destructive actions are marked in red.')}
            />

            {insecureUrl && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={(
                        <Button color="inherit" size="small" onClick={fixScheme} disabled={fixingScheme}>
                            {fixingScheme ? <CircularProgress size={16} color="inherit" /> : tr('cfg_act_https_fix', 'Switch to https')}
                        </Button>
                    )}
                >
                    {tr('cfg_act_https_warning', 'This webshop is registered with an http:// URL. Calls still work, but the URL should be https everywhere.')}
                </Alert>
            )}

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
                gap: 1.5,
            }}>
                {ACTIONS.filter(a => (!a.superOnly || props.login?.sft_group === 'SUPER') && (!a.sftOnly || isSftUser) && can(actionPermKey(a.id))).map((action) => {
                    const Icon = action.icon;
                    // An action is "wired" if it has a path or a custom onClick.
                    // Cards that have neither stay visible (so we don't lose the
                    // intent) but the Run button is disabled with a hint.
                    const wired = !!(action.path || action.onClick);
                    // Actions that run through their own async handler report
                    // progress via `busy()`.
                    const actionBusy = action.busy ? action.busy() : false;
                    const disabled = !props.selectedWebshop || !wired;
                    return (
                        <Paper
                            key={action.id}
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${borderColor}`,
                                backgroundColor: cardBg,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                transition: 'box-shadow 150ms, transform 150ms, border-color 150ms',
                                '&:hover': {
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                                    transform: 'translateY(-2px)',
                                    borderColor: action.destructive ? Colors.def_red : Colors.sft_blue,
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={iconBoxSx(isDark, action.destructive ? Colors.def_red : Colors.sft_blue)}>
                                    <Icon />
                                </Box>
                                {!wired && (
                                    <Chip label={tr('cfg_act_not_wired_chip', 'Not wired yet')} size="small" variant="outlined" sx={{ ml: 'auto' }} />
                                )}
                            </Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textColor, fontSize: '0.9375rem' }}>
                                {action.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: mutedText, flexGrow: 1, lineHeight: 1.45, fontSize: '0.85rem' }}>
                                {action.description}
                            </Typography>
                            <Tooltip title={!props.selectedWebshop ? tr('cfg_act_tip_select_webshop', 'Select a webshop first') : (!wired ? tr('cfg_act_tip_no_endpoint', 'No backend endpoint yet') : '')}>
                                <span style={{ alignSelf: 'flex-start' }}>
                                    <Button
                                        size="small"
                                        variant={action.destructive ? 'outlined' : 'contained'}
                                        color={action.destructive ? 'error' : 'primary'}
                                        disabled={disabled || actionBusy}
                                        startIcon={actionBusy ? <CircularProgress size={14} color="inherit" /> : undefined}
                                        onClick={() => {
                                            if (action.onClick) action.onClick();
                                            else openLegacyAction(action.path);
                                        }}
                                        sx={{ mt: 0.5, textTransform: 'none' }}
                                    >
                                        {action.runLabel || tr('cfg_act_run', 'Run')}
                                    </Button>
                                    {action.secondary && (() => {
                                        const secBusy = action.secondary.busy ? action.secondary.busy() : false;
                                        return (
                                            <Tooltip title={action.secondary.tip || ''}>
                                                <span>
                                                    <Button size="small" variant="outlined" disabled={disabled || secBusy}
                                                        startIcon={secBusy ? <CircularProgress size={14} color="inherit" /> : undefined}
                                                        onClick={action.secondary.onClick} sx={{ mt: 0.5, ml: 1, textTransform: 'none' }}
                                                        data-action-secondary={action.id}>
                                                        {action.secondary.label}
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        );
                                    })()}
                                </span>
                            </Tooltip>
                        </Paper>
                    );
                })}
            </Box>

            <ResendMailDialog
                open={resendModalOpen}
                onClose={() => setResendModalOpen(false)}
                t={props.t}
                proxy={proxyCall}
            />

            <Dialog
                open={updateUrlModalOpen}
                onClose={() => setUpdateUrlModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {tr('cfg_act_update_url_title', 'Update webshop URL')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: mutedText, mt: 0.5 }}>
                        {tr('cfg_act_update_url_dialog_desc2', 'Rewrites the old domain everywhere it is stored on the shop (settings, banners, carousel, image grid, external links, statics and cheque layouts), regenerates the statics, then updates the licence database. Rewriting cannot be undone from here — make sure the new domain already serves the shop.')}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
                        <TextField
                            label={tr('cfg_act_current_url', 'Current URL')}
                            size="small"
                            fullWidth
                            value={baseUrl}
                            disabled
                        />
                        <TextField
                            label={tr('cfg_act_new_url', 'New URL')}
                            size="small"
                            fullWidth
                            value={newUrl}
                            onChange={(e) => setNewUrl(e.target.value)}
                            placeholder="https://shop.example.com"
                            autoFocus
                        />
                        <Typography variant="caption" sx={{ color: mutedText }}>
                            {tr('cfg_act_url_crontab_note', 'Cron jobs for the new domain are set up at the hoster; the old lines are removed here.')}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setUpdateUrlModalOpen(false)} disabled={urlBusy} sx={{ textTransform: 'none' }}>{tr('cfg_act_cancel', 'Cancel')}</Button>
                    <Button variant="contained" color="error" onClick={updateWebshopUrl} disabled={urlBusy}
                        startIcon={urlBusy ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ textTransform: 'none' }}>
                        {tr('cfg_act_update_url_btn', 'Update URL')}
                    </Button>
                </DialogActions>
            </Dialog>

            {props.login?.sft_group === 'SUPER' && (
                <TestVersionDialog
                    open={testVerOpen}
                    onClose={() => setTestVerOpen(false)}
                    t={props.t}
                    api={props.api}
                    login={props.login}
                    webshop={props.selectedWebshop}
                    notify={notify}
                />
            )}
            {isSftUser && (
                <CloneWebshopDialog
                    open={cloneOpen}
                    onClose={() => setCloneOpen(false)}
                    t={props.t}
                    api={props.api}
                    login={props.login}
                    target={props.selectedWebshop}
                    webshops={props.availableWebshops || []}
                    notify={notify}
                />
            )}
        </div>
    );
};

export default WebshopActionsScreen;
